import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { openNotification } from 'components/notification';
import { useTranslation } from 'react-i18next';
import { saveContentMutation } from 'services/content/saveMutation';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { QueryKeys } from 'utils/queryKeys';
import { trackError } from '@proposify/frontend-observability';

export function useSaveSnippetMutation({ onSuccess, onError }: { onSuccess?: () => void; onError?: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const contentId = useSelector(selectContentId);

  const { mutate, isPending } = useMutation({
    ...saveContentMutation,
    onSuccess: () => {
      openNotification({
        title: t('library.save_snippet_success_message'),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: QueryKeys.contentLibrarySnippetsFolders });
      onSuccess?.();
    },
    onError: (error: Error) => {
      trackError(error);
      openNotification({
        title: t('library.save_snippet_error_message'),
        type: 'error',
      });
      onError?.();
    },
  });

  const saveSnippet = ({ name, blockId, folderId }: { name: string; blockId: string; folderId: string }) =>
    mutate({ contentId, name, type: 'SNIPPET', folderId, sourceBlockId: blockId });

  return { saveSnippet, isLoading: isPending };
}
